import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { ListRenderItem } from '@shopify/flash-list';
import { Card } from 'components/Card';
import { CardGeneric } from 'components/CardGeneric';
import { CardRow } from 'components/CardRow';
import { ICONS } from 'components/Icon';
import { NavigationStacks, Screens, Stacks } from 'components/Navigation/types';
import { NavigationHeader } from 'components/NavigationHeader';
import { PageFlatList, PageScrollView } from 'components/Page';
import { SeparatorHorizontal } from 'components/SeparatorHorizontal';
import { Txt } from 'components/Txt';
import { useScreenSize } from 'hooks/useScreenSize';
import { useSelector } from 'hooks/useSelector';
import { ScreenSize } from 'lib/constants';
import { theme } from 'lib/theme';
import { sortBy } from 'lodash';
import React from 'react';
import { useDispatch } from 'react-redux';
import { doSettingsAseCheckOpen } from 'store/actions';

import {
  selectRemoteComplianceSubtypesWithType,
  selectRemoteSettingsAssessmentChecks,
} from 'store/remote/selectors';

type Props = NativeStackScreenProps<
  NavigationStacks[Stacks.SETTINGS],
  Screens.SettingsAssessmentCompliance
>;

type CheckRow =
  | {
      type: 'sectionTitle';
      key: string;
      data: {
        name: string;
      };
    }
  | {
      type: 'groupTitle';
      key: string;
      data: {
        firstInSection: boolean;
        name: string;
      };
    }
  | {
      type: 'check';
      key: string;
      data: {
        firstInGroup: boolean;
        lastInGroup: boolean;
        id: string;
        name: string;
        type: { id: string; name: string };
        complianceTypeName: string | null;
      };
    };

export const ChecksListScreen: React.FC<{
  navigation: Props['navigation'];
}> = ({ navigation }) => {
  const dispatch = useDispatch();
  const screenSize = useScreenSize();

  const complianceSubtypes = useSelector(
    selectRemoteComplianceSubtypesWithType,
  )!;

  const title = 'Element Checks';
  const checks = useSelector(selectRemoteSettingsAssessmentChecks) || {};

  let currentSection: string = '';
  let currentGroup: string = '';
  const finalCheckRows = sortBy(Object.values(checks), [
    'field.section.name',
    'field.group.name',
    'name',
  ]).reduce<CheckRow[]>((acc, check) => {
    let isNewSection: boolean = false;
    let isNewGroup: boolean = false;
    if (check.field.section.name !== currentSection) {
      currentSection = check.field.section.name;
      currentGroup = '';
      acc.push({
        type: 'sectionTitle',
        key: `sectionTitle-${check.field.section.name}`,
        data: {
          name: check.field.section.name,
        },
      });
      isNewSection = true;
    }
    if (check.field.group.name !== currentGroup) {
      currentGroup = check.field.group.name;
      acc.push({
        type: 'groupTitle',
        key: `groupTitle-${check.field.group.name}`,
        data: {
          firstInSection: isNewSection,
          name: check.field.group.name,
        },
      });
      isNewGroup = true;
    }
    const previousAcc = acc[acc.length - 1];
    if (previousAcc && previousAcc.type === 'check') {
      previousAcc.data.lastInGroup = false;
    }
    acc.push({
      type: 'check',
      key: `check-${check.id}`,
      data: {
        firstInGroup: isNewGroup,
        lastInGroup: true,
        id: check.id,
        name: check.name,
        type: { id: check.type.id, name: check.type.name },
        complianceTypeName: check.assessmentTypeId
          ? complianceSubtypes[check.assessmentTypeId]?.name || null
          : null,
      },
    });
    return acc;
  }, []);

  const renderItem: ListRenderItem<CheckRow> = React.useCallback(
    ({ item, index }) => {
      if (item.type === 'sectionTitle') {
        return (
          <>
            {index !== 0 && (
              <SeparatorHorizontal
                withMarginBottom
                style={{ marginTop: theme.paddingVertical }}
              />
            )}
            <CardGeneric>
              <Txt size="xlarge">{item.data.name}</Txt>
            </CardGeneric>
          </>
        );
      }
      if (item.type === 'groupTitle') {
        return (
          <Card
            label={item.data.name}
            margin={false}
            marginTop={!item.data.firstInSection}
          />
        );
      }
      if (item.type === 'check') {
        return (
          <>
            {!item.data.firstInGroup && (
              <SeparatorHorizontal
                marginVertical={0}
                marginHorizontal={theme.paddingHorizontal}
              />
            )}
            <Card
              margin={false}
              borderRadiusTop={item.data.firstInGroup}
              borderRadiusBottom={item.data.lastInGroup}
            >
              <CardRow
                label={item.data.name}
                label2={item.data.type.name}
                labelAlignTop
                value={
                  item.data.complianceTypeName
                    ? `Linked to ${item.data.complianceTypeName}`
                    : undefined
                }
                onPress={() =>
                  dispatch(doSettingsAseCheckOpen({ id: item.data.id }))
                }
              />
            </Card>
          </>
        );
      }
      return null;
    },
    [dispatch],
  );

  if (!Object.keys(checks).length) {
    return (
      <PageScrollView
        isInModal={false}
        header={
          <NavigationHeader
            heading={title}
            onPressBackButton={
              screenSize === ScreenSize.small ? navigation.goBack : undefined
            }
          />
        }
      >
        <Card explanation="Checks added to field groups will appear here." />
      </PageScrollView>
    );
  }

  return (
    <PageFlatList
      header={<NavigationHeader heading={title} />}
      emptyConfig={{
        icon: ICONS.RBPlectrumLight,
        text: 'No Questions or Checklists',
      }}
      contentPaddingTop={theme.paddingVertical - theme.paddingHorizontalHalf}
      listConfig={{
        data: finalCheckRows,
        keyExtractor: (item) => item.key,
        getItemType: (item) => item.type,
        renderItem,
        ItemSeparatorComponent: undefined,
      }}
    />
  );
};
